import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import {
  type TimeFilter,
  ALL_TIME,
  isTimeFilterActive,
} from '@/lib/timeFilter'
import { Clock, Minus, Plus, Check, ChevronRight } from 'lucide-react'

interface Props {
  value: TimeFilter
  onChange: (f: TimeFilter) => void
  /** Mirror of the topbar `toolbar_labels` pref — when false the button
   *  shows only the icon (+ active dot), no text label. */
  toolbarLabels: boolean
  /** Compact mode — icon-only trigger sized to sit inside the search bar
   *  next to the label filter. Colour conveys the active state; the
   *  summary moves to the button tooltip. */
  compact?: boolean
  /** Fired when the dropdown opens — lets the parent dismiss any other
   *  popover (e.g. the live search results) so they don't overlap. */
  onOpen?: () => void
}

/** Short label shown on the button when a filter is active. Kept terse so
 *  the toolbar stays compact; the full phrasing lives inside the popover. */
function summarize(
  f: TimeFilter,
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  switch (f.kind) {
    case 'all':
      return t('time.all')
    case 'rolling':
      return f.unit === 'hour'
        ? t('time.lastHoursShort', { count: f.count })
        : t('time.lastDaysShort', { count: f.count })
    case 'calendar':
      return t(`time.sum.${f.bucket}`)
    case 'previous':
      return t(`time.sumPrev.${f.bucket}`)
    case 'range':
      return t('time.rangeShort')
    default:
      return t('time.all')
  }
}

/** Temporal board filter — « depuis combien de temps la carte est là ».
 *  A dropdown that combines rolling windows (last N hours/days, with a
 *  spinner), current-period buckets (today / this week / this month),
 *  isolated past buckets (yesterday / last week / last month — the
 *  « seulement » semantics) and a custom date range. */
export function TimeFilterMenu({
  value,
  onChange,
  toolbarLabels,
  compact = false,
  onOpen,
}: Props) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement | null>(null)

  // Local spinner counts — seeded from the active filter so reopening the
  // menu shows what's in effect, but editable even when another option is
  // currently selected (so the user can dial in « 6 heures » before
  // committing).
  const [hourCount, setHourCount] = useState(
    value.kind === 'rolling' && value.unit === 'hour' ? value.count : 1,
  )
  const [dayCount, setDayCount] = useState(
    value.kind === 'rolling' && value.unit === 'day' ? value.count : 1,
  )
  const [rangeFrom, setRangeFrom] = useState(
    value.kind === 'range' ? value.from : '',
  )
  const [rangeTo, setRangeTo] = useState(value.kind === 'range' ? value.to : '')

  useEffect(() => {
    if (!open) return
    function onDown(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  const active = isTimeFilterActive(value)

  const isHourSel = value.kind === 'rolling' && value.unit === 'hour'
  const isDaySel = value.kind === 'rolling' && value.unit === 'day'
  const isCal = (b: 'today' | 'yesterday' | 'week' | 'week7' | 'month') =>
    value.kind === 'calendar' && value.bucket === b
  const isPrev = (b: 'yesterday' | 'lastweek' | 'lastmonth') =>
    value.kind === 'previous' && value.bucket === b
  const isRange = value.kind === 'range'

  function clampHour(n: number) {
    return Math.min(72, Math.max(1, n))
  }
  function clampDay(n: number) {
    return Math.min(365, Math.max(1, n))
  }

  function toggleOpen() {
    setOpen((v) => {
      const next = !v
      if (next) onOpen?.()
      return next
    })
  }

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        onClick={toggleOpen}
        aria-pressed={active}
        title={active ? summarize(value, t) : t('time.title')}
        className={
          compact
            ? cn(
                'inline-flex items-center gap-1 rounded-lg border px-2 py-1.5 transition-colors',
                active
                  ? 'border-indigo-200 bg-indigo-100 text-indigo-700'
                  : 'border-[var(--color-border-soft)] bg-[var(--color-surface-2)] text-[var(--color-text-dim)] hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]',
              )
            : cn(
                'inline-flex items-center gap-1.5 rounded-lg border border-[var(--color-border-soft)] bg-[var(--color-surface)] px-2 py-1.5 text-[12.5px] font-medium transition-colors',
                active
                  ? 'border-indigo-200 bg-indigo-100 text-indigo-700'
                  : 'text-[var(--color-text-dim)] hover:bg-[var(--color-surface-2)] hover:text-[var(--color-text)]',
              )
        }
      >
        <Clock className="h-4 w-4" />
        {!compact && (toolbarLabels || active) && (
          <span className="max-w-[120px] truncate text-[12.5px]">
            {active ? summarize(value, t) : t('time.label')}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-[calc(100%+4px)] z-30 w-80 overflow-hidden rounded-xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] shadow-[var(--shadow-modal)]">
          <div className="flex items-center justify-between gap-2 border-b border-[var(--color-border-soft)] px-3 py-2.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--color-text-dim)]">
              {t('time.heading')}
            </span>
            {active && (
              <button
                type="button"
                onClick={() => onChange(ALL_TIME)}
                className="text-[10.5px] font-medium text-[var(--color-text-dim)] transition-colors hover:text-[var(--color-text)]"
              >
                {t('time.reset')}
              </button>
            )}
          </div>

          <div className="max-h-[70vh] overflow-y-auto px-2 py-2">
            {/* Default — all time */}
            <OptionRow
              selected={value.kind === 'all'}
              label={t('time.all')}
              onClick={() => {
                onChange(ALL_TIME)
                setOpen(false)
              }}
            />

            {/* Rolling windows with spinners */}
            <SectionLabel>{t('time.rollingHeading')}</SectionLabel>
            <SpinnerRow
              selected={isHourSel}
              prefix={t('time.lastPrefix')}
              suffix={t('time.hoursSuffix', { count: hourCount })}
              count={hourCount}
              onDec={() => {
                const n = clampHour(hourCount - 1)
                setHourCount(n)
                onChange({ kind: 'rolling', unit: 'hour', count: n })
              }}
              onInc={() => {
                const n = clampHour(hourCount + 1)
                setHourCount(n)
                onChange({ kind: 'rolling', unit: 'hour', count: n })
              }}
              onSelect={() =>
                onChange({ kind: 'rolling', unit: 'hour', count: hourCount })
              }
            />
            <SpinnerRow
              selected={isDaySel}
              prefix={t('time.lastPrefix')}
              suffix={t('time.daysSuffix', { count: dayCount })}
              count={dayCount}
              onDec={() => {
                const n = clampDay(dayCount - 1)
                setDayCount(n)
                onChange({ kind: 'rolling', unit: 'day', count: n })
              }}
              onInc={() => {
                const n = clampDay(dayCount + 1)
                setDayCount(n)
                onChange({ kind: 'rolling', unit: 'day', count: n })
              }}
              onSelect={() =>
                onChange({ kind: 'rolling', unit: 'day', count: dayCount })
              }
            />

            {/* Current calendar period — cumulative « depuis … jusqu'à
                maintenant ». Each entry includes everything up to now. */}
            <SectionLabel>{t('time.currentHeading')}</SectionLabel>
            <OptionRow
              selected={isCal('today')}
              label={t('time.today')}
              onClick={() => {
                onChange({ kind: 'calendar', bucket: 'today' })
                setOpen(false)
              }}
            />
            <OptionRow
              selected={isCal('yesterday')}
              label={t('time.yesterdayCumul')}
              onClick={() => {
                onChange({ kind: 'calendar', bucket: 'yesterday' })
                setOpen(false)
              }}
            />
            <OptionRow
              selected={isCal('week')}
              label={t('time.weekStart')}
              onClick={() => {
                onChange({ kind: 'calendar', bucket: 'week' })
                setOpen(false)
              }}
            />
            <OptionRow
              selected={isCal('week7')}
              label={t('time.weekRolling')}
              onClick={() => {
                onChange({ kind: 'calendar', bucket: 'week7' })
                setOpen(false)
              }}
            />
            <OptionRow
              selected={isCal('month')}
              label={t('time.month')}
              onClick={() => {
                onChange({ kind: 'calendar', bucket: 'month' })
                setOpen(false)
              }}
            />

            {/* Isolated past bucket — the « seulement » semantics */}
            <SectionLabel>{t('time.previousHeading')}</SectionLabel>
            <OptionRow
              selected={isPrev('yesterday')}
              label={t('time.yesterday')}
              onClick={() => {
                onChange({ kind: 'previous', bucket: 'yesterday' })
                setOpen(false)
              }}
            />
            <OptionRow
              selected={isPrev('lastweek')}
              label={t('time.lastweek')}
              onClick={() => {
                onChange({ kind: 'previous', bucket: 'lastweek' })
                setOpen(false)
              }}
            />
            <OptionRow
              selected={isPrev('lastmonth')}
              label={t('time.lastmonth')}
              onClick={() => {
                onChange({ kind: 'previous', bucket: 'lastmonth' })
                setOpen(false)
              }}
            />

            {/* Custom date range */}
            <SectionLabel>{t('time.rangeHeading')}</SectionLabel>
            <div className="px-2 py-1">
              <div className="flex items-center gap-1.5">
                <input
                  type="date"
                  value={rangeFrom}
                  max={rangeTo || undefined}
                  onChange={(e) => setRangeFrom(e.target.value)}
                  className="min-w-0 flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-1.5 py-1 text-[11.5px] text-[var(--color-text)] outline-none focus:border-[var(--color-link)]"
                />
                <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-dim)]" />
                <input
                  type="date"
                  value={rangeTo}
                  min={rangeFrom || undefined}
                  onChange={(e) => setRangeTo(e.target.value)}
                  className="min-w-0 flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-1.5 py-1 text-[11.5px] text-[var(--color-text)] outline-none focus:border-[var(--color-link)]"
                />
              </div>
              <button
                type="button"
                disabled={!rangeFrom || !rangeTo}
                onClick={() => {
                  if (!rangeFrom || !rangeTo) return
                  onChange({ kind: 'range', from: rangeFrom, to: rangeTo })
                  setOpen(false)
                }}
                className={cn(
                  'mt-1.5 w-full rounded-md px-2 py-1 text-[11.5px] font-semibold transition-colors',
                  isRange
                    ? 'bg-indigo-100 text-indigo-700'
                    : 'bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-40',
                )}
              >
                {t('time.rangeApply')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <>
      {/* Grey relief separator isolating each temporal category — a 1px
          grey line topped by a faint light ridge so the groups read as
          embossed, clearly-bounded zones. */}
      <div
        className="mx-1 mb-1 mt-2 h-px bg-[var(--color-border)]"
        style={{ boxShadow: '0 1px 0 0 var(--color-surface-2)' }}
        aria-hidden
      />
      <div className="px-2 pb-1 pt-0.5 text-[9.5px] font-semibold uppercase tracking-wider text-[var(--color-text-dim)]">
        {children}
      </div>
    </>
  )
}

function OptionRow({
  selected,
  label,
  onClick,
}: {
  selected: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-[12.5px] transition-colors',
        selected
          ? 'bg-indigo-50 font-medium text-indigo-700'
          : 'text-[var(--color-text)] hover:bg-[var(--color-surface-2)]',
      )}
    >
      <span className="truncate">{label}</span>
      {selected && <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={3} />}
    </button>
  )
}

/** A rolling-window row with a value the user can spin up/down. Tapping the
 *  text selects the option at the current count; the ± buttons both adjust
 *  AND select (so dialing the spinner immediately applies). */
function SpinnerRow({
  selected,
  prefix,
  suffix,
  count,
  onDec,
  onInc,
  onSelect,
}: {
  selected: boolean
  prefix: string
  suffix: string
  count: number
  onDec: () => void
  onInc: () => void
  onSelect: () => void
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 rounded-lg px-2 py-1 transition-colors',
        selected ? 'bg-indigo-50' : 'hover:bg-[var(--color-surface-2)]',
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          'flex min-w-0 flex-1 items-center gap-1.5 text-left text-[12.5px]',
          selected ? 'font-medium text-indigo-700' : 'text-[var(--color-text)]',
        )}
      >
        {selected && <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={3} />}
        <span className="truncate">
          {prefix} <span className="tabular-nums">{count}</span> {suffix}
        </span>
      </button>
      <div className="flex shrink-0 items-center gap-0.5">
        <button
          type="button"
          onClick={onDec}
          aria-label="-1"
          className="inline-flex h-5 w-5 items-center justify-center rounded-md border border-[var(--color-border)] text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
        >
          <Minus className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={onInc}
          aria-label="+1"
          className="inline-flex h-5 w-5 items-center justify-center rounded-md border border-[var(--color-border)] text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
        >
          <Plus className="h-3 w-3" />
        </button>
      </div>
    </div>
  )
}
